import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CurrencyBtc, SignOut, Wallet as WalletIcon, ChartLine, Ranking, Briefcase, CaretDown, ArrowCircleUp, ArrowCircleDown } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import DepositPanel from "./DepositPanel";
import WithdrawPanel from "./WithdrawPanel";
import { useEffect, useState } from "react";
import api, { fmtUsd } from "../lib/api";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [wallet, setWallet] = useState({ balance: 0, equity: 0, unrealized_pnl: 0 });
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const reloadWallet = () => api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get("/wallet").then((r) => alive && setWallet(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 1500);
    return () => { alive = false; clearInterval(i); };
  }, []);

  const isTelegram = typeof window !== "undefined" && !!window.Telegram?.WebApp?.initData;
  const displayName = user?.name || user?.email || "Trader";
  const initials = (displayName.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("") || "U").toUpperCase();

  const navItem = (to, label, Icon) => {
    const active = location.pathname.startsWith(to);
    return (
      <Link to={to} data-testid={`nav-${label.toLowerCase()}`}
        className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1.5 rounded-md text-xs sm:text-sm font-medium tracking-wide transition-colors duration-200 ${active ? "bg-white/[0.06] text-[#E3B341] ring-1 ring-inset ring-white/[0.08]" : "text-[#6B7280] hover:text-white hover:bg-white/[0.06]"}`}>
        <Icon size={18} weight={active ? "fill" : "bold"} className="" />
        <span className="hidden xs:inline sm:inline">{label}</span>
      </Link>
    );
  };

  return (
    <nav className="h-12 sm:h-14 bg-[#0A0E17] border-b border-[#1F2937] flex items-center justify-between px-2 sm:px-4 shrink-0">
      <div className="flex items-center h-full gap-1 sm:gap-1.5 min-w-0">
        <Link to="/trade" className="flex items-center gap-2 pr-2 sm:pr-4 sm:mr-2 shrink-0">
          <div className="h-7 w-7 sm:h-8 sm:w-8 bg-[#FCD535] flex items-center justify-center rounded-sm">
            <CurrencyBtc size={16} weight="bold" className="text-black" />
          </div>
          <span className="font-semibold tracking-tight text-sm sm:text-base hidden sm:inline">TRADE</span>
        </Link>
        {navItem("/trade", "Trade", ChartLine)}
        {navItem("/portfolio", "Portfolio", WalletIcon)}
        {navItem("/leaderboard", "Top", Ranking)}
        {navItem("/prop", "Prop", Briefcase)}
      </div>

      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        <div className="flex flex-col items-end" data-testid="navbar-equity">
          <span className="overline hidden sm:inline">Equity</span>
          <span className="mono text-xs sm:text-sm font-semibold">${fmtUsd(wallet.equity)}</span>
        </div>
        <div className="hidden md:flex flex-col items-end" data-testid="navbar-pnl">
          <span className="overline">Unrealized</span>
          <span className={`mono text-sm font-semibold ${wallet.unrealized_pnl >= 0 ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
            {wallet.unrealized_pnl >= 0 ? "+" : ""}${fmtUsd(wallet.unrealized_pnl)}
          </span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 pl-2 sm:pl-3 border-l border-[#1F2937] outline-none group h-9"
              data-testid="account-menu-trigger">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FCD535]/[0.14] text-[#E3B341] ring-1 ring-inset ring-white/[0.08] text-xs font-bold shrink-0"
                data-testid="navbar-username">{initials}</span>
              <span className="text-sm text-[#D1D5DB] hidden lg:inline max-w-[120px] truncate">{displayName}</span>
              <CaretDown size={12} weight="bold" className="text-[#6B7280] hidden lg:inline transition-transform group-data-[state=open]:rotate-180" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8}
            className="w-56 max-w-[calc(100vw-24px)] bg-[#0D1117] border border-[#1F2937] text-white shadow-xl"
            data-testid="account-menu">
            <DropdownMenuLabel className="pb-1 font-normal">
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#FCD535]/[0.14] text-[#E3B341] ring-1 ring-inset ring-white/[0.08] text-sm font-bold shrink-0">{initials}</span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{displayName}</div>
                  {user?.email && <div className="text-[11px] text-[#6B7280] truncate">{user.email}</div>}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#1F2937]" />
            <DropdownMenuItem onClick={() => navigate("/portfolio")}
              className="cursor-pointer focus:bg-[#1F2937] focus:text-white" data-testid="account-menu-portfolio">
              <WalletIcon size={16} className="mr-2 text-[#9CA3AF]" /> Portfolio
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/prop")}
              className="cursor-pointer focus:bg-[#1F2937] focus:text-white" data-testid="account-menu-prop">
              <Briefcase size={16} className="mr-2 text-[#9CA3AF]" /> Prop Account
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#1F2937]" />
            <DropdownMenuItem onClick={() => setDepositOpen(true)}
              className="cursor-pointer focus:bg-[#1F2937] focus:text-white" data-testid="account-menu-deposit">
              <ArrowCircleUp size={16} className="mr-2 text-[#0ECB81]" /> Deposit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setWithdrawOpen(true)}
              className="cursor-pointer focus:bg-[#1F2937] focus:text-white" data-testid="account-menu-withdraw">
              <ArrowCircleDown size={16} className="mr-2 text-[#FCD535]" /> Withdraw
            </DropdownMenuItem>
            {!isTelegram && (
              <>
                <DropdownMenuSeparator className="bg-[#1F2937]" />
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }}
                  className="cursor-pointer text-[#F6465D] focus:bg-[#F6465D]/10 focus:text-[#F6465D]" data-testid="logout-button">
                  <SignOut size={16} className="mr-2" /> Log out
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <DepositPanel hideTrigger controlledOpen={depositOpen} onOpenChange={setDepositOpen} />
        <WithdrawPanel hideTrigger controlledOpen={withdrawOpen} onOpenChange={setWithdrawOpen} balance={wallet.balance} onChanged={reloadWallet} />
      </div>
    </nav>
  );
}
